/**
 * Меню Блока — «три точки» в дальнем верхнем углу карточки (правка 2026-08-30).
 *
 * Всё, что человек делает с Блоком целиком, собрано в одном месте. До этой правки часть
 * действий висела кнопками на карточке и появлялась только в режиме сборки: свернуть или
 * покрасить Блок было нечем вовсе, а убрать его — только включив сборку. Одно меню на
 * карточке решает и это, и обратную беду: шапка Блока не обрастает пятью значками, между
 * которыми надо целиться.
 *
 * Про клавиатуру. Меню — это `menu` с пунктами, а не набор кнопок рядом: диктор объявляет
 * «меню, пункт такой-то из шести», стрелки ходят по пунктам, Esc закрывает и возвращает
 * фокус на сами три точки. Переключатели («свернуть», «закрепить») объявляются нажатыми —
 * иначе, открыв меню второй раз, человек не узнал бы, что Блок уже свёрнут.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import type { BlockView } from '@core/layout/editor.js';
import { useT } from '../state/settings.js';

export interface BlockMenuProps {
  /** Название Блока — оно уходит в подпись кнопки: «Что сделать с Блоком: Переписка». */
  readonly title: string;
  readonly view: BlockView;
  /** Убрать Блок из Канала может только тот, кто правит общую Раскладку. */
  readonly onRemove?: () => void;
  /** Взять Блок и понести — тот же захват, что и с клавиатуры. */
  readonly onMove?: () => void;
  /**
   * Взять Блок на размер: дальше стрелки делают его шире, уже, выше и ниже. Путь через
   * меню нужен наравне с уголком в углу карточки — в уголок надо попасть мышью, а он
   * маленький, и человеку с плохим зрением растянуть Блок было нечем (правка 2026-08-31).
   */
  readonly onResize?: () => void;
  /** Свернуть, скрыть, закрепить — правки личного вида, они идут одним путём. */
  readonly onView: (patch: { collapsed?: boolean; hidden?: boolean; pinned?: boolean }) => void;
  /** Открыть краски и размер букв. Нет обработчика — пункта в меню нет. */
  readonly onSettings?: () => void;
  /** Идёт разговор с сервером: второй правки не начинают. */
  readonly busy?: boolean;
}

interface Item {
  readonly key: string;
  readonly label: string;
  /** Переключатель — объявляется нажатым или отжатым. Обычное действие — просто пункт. */
  readonly checked?: boolean;
  readonly danger?: boolean;
  readonly run: () => void;
}

export function BlockMenu({
  title,
  view,
  onRemove,
  onMove,
  onResize,
  onView,
  onSettings,
  busy = false,
}: BlockMenuProps): ReactNode {
  const t = useT();
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  /** Какой пункт под фокусом. Меню открывается с первого — руку с клавиатуры не ищут. */
  const [at, setAt] = useState(0);

  const close = useCallback(
    (backToButton: boolean): void => {
      setOpen(false);
      // Фокус возвращается на три точки: не верни мы его, следующий Tab начал бы обход
      // с начала страницы, и человек с клавиатуры терял бы место после каждого действия.
      if (backToButton) buttonRef.current?.focus();
    },
    [],
  );

  // Нажатие мимо меню закрывает его. Слушаем нажатие, а не отпускание: отпускание приходит
  // уже после того, как браузер увёл фокус, и меню мигало бы закрытием после действия.
  useEffect(() => {
    if (!open) return;
    const away = (event: globalThis.PointerEvent): void => {
      const target = event.target as Node | null;
      if (menuRef.current?.contains(target ?? null)) return;
      if (buttonRef.current?.contains(target ?? null)) return;
      setOpen(false);
    };
    window.addEventListener('pointerdown', away);
    return () => window.removeEventListener('pointerdown', away);
  }, [open]);

  // Порядок нарочно такой: сперва обратимое, «удалить» — последним и за чертой. Стоя оно
  // между «скрыть» и «перенести», как было задумано сначала, оно ловило бы человека с
  // клавиатуры: одна стрелка вниз от безобидного «скрыть» — и Enter уносит Блок у всей фирмы.
  const items: Item[] = [
    {
      key: 'collapse',
      label: view.collapsed ? t('block.expand') : t('block.collapse'),
      checked: view.collapsed,
      run: () => onView({ collapsed: !view.collapsed }),
    },
    {
      key: 'hide',
      label: t('block.hide'),
      run: () => onView({ hidden: true }),
    },
    ...(onMove ? [{ key: 'move', label: t('block.carry'), run: onMove }] : []),
    ...(onResize ? [{ key: 'resize', label: t('layout.resize'), run: onResize }] : []),
    {
      key: 'pin',
      label: view.pinned ? t('block.unpin') : t('block.pin'),
      checked: view.pinned,
      run: () => onView({ pinned: !view.pinned }),
    },
    ...(onSettings ? [{ key: 'settings', label: t('block.settings'), run: onSettings }] : []),
    ...(onRemove ? [{ key: 'remove', label: t('layout.removeBlock'), danger: true, run: onRemove }] : []),
  ];
  // Пункт мог исчезнуть при открытом меню (сменилась роль, Блок закрепили) — тогда запомненное
  // место оказывается за краем, и Tab не находил бы в меню ни одной точки входа.
  const cursor = Math.min(at, items.length - 1);

  // Пункты приходят и уходят (у Сотрудника нет «удалить»), а запомненное место могло
  // остаться за краем списка — тогда стрелка увела бы фокус в пустоту.
  const focusAt = (index: number): void => {
    const safe = ((index % items.length) + items.length) % items.length;
    setAt(safe);
    const node = menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"], [role="menuitemcheckbox"]')[safe];
    node?.focus();
  };

  useEffect(() => {
    if (!open) return;
    // Открыли — фокус на первый пункт. Через кадр: до отрисовки фокусировать нечего.
    const timer = window.setTimeout(() => focusAt(0), 0);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onMenuKey = (event: KeyboardEvent<HTMLDivElement>): void => {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        close(true);
        return;
      case 'ArrowDown':
        event.preventDefault();
        focusAt(cursor + 1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        focusAt(cursor - 1);
        return;
      case 'Home':
        event.preventDefault();
        focusAt(0);
        return;
      case 'End':
        event.preventDefault();
        focusAt(items.length - 1);
        return;
      case 'Tab':
        // Уход с клавиатуры закрывает меню, но фокус не отбираем: человек ведёт его дальше
        // сам, и вернуть его на кнопку значило бы поймать его в ловушку.
        setOpen(false);
        return;
      default:
    }
  };

  return (
    <div className="relative shrink-0">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`${t('layout.blockMenu')}: ${title}`}
        title={t('layout.blockMenu')}
        onClick={() => setOpen((was) => !was)}
        onKeyDown={(event) => {
          // Стрелка вниз на закрытом меню — обычный способ открыть его с клавиатуры.
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className="inline-flex items-center justify-center rounded-md border border-transparent px-2 py-1 text-ink-soft transition-colors hover:bg-surface-2 hover:text-ink"
      >
        {/* Три точки рисуем буквами, а не картинкой: они тянутся вместе с размером текста
            из Настроек и красятся цветом кнопки. */}
        <span aria-hidden="true" className="leading-none">
          ⋯
        </span>
      </button>

      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={`${t('layout.blockMenu')}: ${title}`}
          onKeyDown={onMenuKey}
          className="absolute end-0 z-20 mt-1 flex min-w-48 flex-col rounded-md border border-line bg-surface py-1 shadow-lg"
        >
          {items.map((item, index) => (
            <button
              key={item.key}
              type="button"
              role={item.checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
              {...(item.checked === undefined ? {} : { 'aria-checked': item.checked })}
              disabled={busy}
              tabIndex={index === cursor ? 0 : -1}
              onClick={() => {
                item.run();
                close(true);
              }}
              className={`w-full px-3 py-1.5 text-start text-sm transition-colors hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                // Черта над «удалить» — не украшение: она даёт глазу зацепку, что дальше идёт
                // необратимое, и отделяет его от привычных обратимых пунктов выше.
                item.danger ? 'mt-1 border-t border-line pt-2 text-danger' : 'text-ink'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
